/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DATA_LAYER_EVENTS, REGISTRATION_STATUSES } from 'components/common/constants';
import PopUpSuccessScheme from 'components/common/PopUpScheme/PopUpSuccessScheme';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import { pushDataLayerEvent } from 'services/analytics';
import { getCustomerId, getCustomerType, getInternalUserId, setIsSuccessLoginStatus } from 'services/authUtils';
import { PopUp } from 'uikit/PopUp/PopUp';
import { trackTelemetryEvent, TELEMETRY_EVENTS } from 'services/telemetry';
import '../RegistrationStyles.scss';

const SuccessAccountRegistration = ({ registrationStore, userStore }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const i18n = useContext(i18nContext);

  useEffect(() => {
    if (
      location.pathname === ROUTE_PATHS.REGISTRATION_SUCCESS &&
      registrationStore.registrationStatus !== REGISTRATION_STATUSES.REGISTRATION_STATUS_CONFIRMED
    ) {
      navigate(ROUTE_PATHS.REGISTRATION);
    } else if (userStore?.userData?.availableAccounts && userStore?.userData?.availableAccounts?.length > 0) {
      pushDataLayerEvent(DATA_LAYER_EVENTS.REGISTRATION_SUCCESS_ADDITIONAL, {
        user_id_internal: getInternalUserId(),
        customer_id: getCustomerId(),
        customer_type: getCustomerType()
      });
    } else {
      pushDataLayerEvent(DATA_LAYER_EVENTS.REGISTRATION_SUCCESS, {
        user_id_internal: getInternalUserId(),
        customer_id: getCustomerId(),
        customer_type: getCustomerType()
      });
    }
    trackTelemetryEvent(TELEMETRY_EVENTS.REGISTRATION_CUSTOMER_SUCCESS);
  }, []);

  const onPopUpClose = async () => {
    if (!registrationStore.isAfterUserRegistration) {
      // For logged-in user after creating new customer - load updated user data
      await userStore.loadUserData(null, true);

      // Find and switch to the newly created customer
      const accountsBeforeStr = sessionStorage.getItem('accountsBeforeRegistration');
      const accountsBefore = accountsBeforeStr ? JSON.parse(accountsBeforeStr) : [];
      const currentAccounts = userStore?.userData?.availableAccounts?.map((acc) => acc.account_number) || [];
      const newAccount = userStore?.userData?.availableAccounts?.find(
        (acc) => !accountsBefore.includes(acc.account_number)
      );

      if (newAccount) {
        await userStore.changeCurrentUserAccount(newAccount.account_number);
      }

      // Clean up session storage
      sessionStorage.removeItem('accountsBeforeRegistration');
      sessionStorage.removeItem('needsUserDataRefresh');

      // Navigate directly to dashboard for logged-in users to see the new customer
      navigate(ROUTE_PATHS.DASHBOARD);
    } else {
      // For new user registration - the customer was just created, so the pre-registration
      // userStore snapshot (fetched while isUserDataFetched was already true) is stale and must
      // be reloaded, otherwise App.js's bootstrap effect skips reloading it after this navigation.
      // getAppState() must be called here too: this is the first non-excluded route this brand-new
      // user's session ever reaches, and loadUserData() alone does not populate appFeatures - without
      // it, getKycProvider()/getKybProvider()/isRepresentativesEnabled() stay empty, leaving the
      // Verification widget blank and the header's customer switcher hidden.
      await userStore.loadUserData(null, true);
      await userStore.getAppState();

      // For new user registration - set login status and go to verification
      setIsSuccessLoginStatus(true);
      navigate(ROUTE_PATHS.VERIFICATION);
    }
    registrationStore.resetRegistrationStore();
  };

  return (
    <PopUp show={true}>
      <PopUpSuccessScheme
        onClose={onPopUpClose}
        message={i18n.getMessage('popUp.success.registration')}
        buttonText={i18n.getMessage('popUp.success.button')}
      />
    </PopUp>
  );
};

SuccessAccountRegistration.propTypes = {
  registrationStore: MobXPropTypes.observableObject,
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  registrationStore: stores.registrationStore,
  userStore: stores.userStore
}))(observer(SuccessAccountRegistration));
